import Enums from "./enums";
import { orderBy, toLower, toUpper } from "lodash";
import { isListNotEmpty, isObjectNotEmpty, isStringNotEmpty } from "./objectUtils";

export const DUPLICATE_BLOCKING_STATUSES = Enums.DuplicateBlockingStatuses.keys();

export const SURVEY_FLOW_KEYS = Object.freeze({
    GOALS: 'GOALS',
    INTAKE: 'INTAKE',
    CONSULT: 'CONSULT',
    FOLLOW_UP: 'FOLLOW_UP'
});

const SURVEY_FLOW_KEY_VALUES = Object.freeze(Object.values(SURVEY_FLOW_KEYS));

const LEGACY_PRODUCT_FAMILY_RULES = [
    {
        familyId: 'SEMAGLUTIDE',
        productSkus: ['SEMAGLUTIDE', 'SEMAGLUTIDE-G', 'SEMAGLUTIDE-GM', 'SEMAGLUTIDE-MICRODOSING', 'SEMAGLUTIDE-G-MICRODOSING', 'OZEMPIC'],
        tokens: ['se', 'seg', 'segm', 'semd', 'segmd', 'oz', 'semaglutide', 'semaglutide-g', 'semaglutide-gm', 'semaglutide-microdosing', 'semaglutide-g-microdosing', 'ozempic']
    },
    {
        familyId: 'TIRZEPATIDE',
        productSkus: ['TIRZEPATIDE', 'TIRZEPATIDE-G', 'TIRZEPATIDE-GM', 'TIRZEPATIDE-MICRODOSING', 'TIRZEPATIDE-G-MICRODOSING', 'ZEPBOUND'],
        tokens: ['ti', 'tig', 'tigm', 'timd', 'tigmd', 'ze', 'tirzepatide', 'tirzepatide-g', 'tirzepatide-gm', 'tirzepatide-microdosing', 'tirzepatide-g-microdosing', 'zepbound']
    },
    {
        familyId: 'GLUTATHIONE',
        productSkus: ['GLUTATHIONE', 'GLUTATHIONE-N'],
        tokens: ['gl', 'gln', 'glutathione', 'glutathione-n']
    },
    {
        familyId: 'NAD_PLUS',
        productSkus: ['NAD-PLUS', 'NAD-PLUS-N'],
        tokens: ['na', 'nan', 'nad-plus', 'nad-plus-n']
    },
    {
        familyId: 'SERMORELIN',
        productSkus: ['SERMORELIN', 'SERMORELIN-ODT'],
        tokens: ['ser', 'sero', 'sermorelin', 'sermorelin-odt']
    },
    {
        familyId: 'B12',
        productSkus: ['MIC-B12', 'B12'],
        tokens: ['lb', 'bc', 'mic-b12', 'b12']
    },
    {
        familyId: 'FACE_CREAM_E',
        productSkus: ['FACE-CREAM-E', 'FACE-CREAM-E-90'],
        tokens: ['fce', 'fce90', 'face-cream-e', 'face-cream-e-90']
    },
    {
        familyId: 'FACE_CREAM_T',
        productSkus: ['FACE-CREAM-T', 'FACE-CREAM-T-90'],
        tokens: ['fct', 'fct90', 'face-cream-t', 'face-cream-t-90', 'face-cream-r', 'face-cream-r-90']
    },
    {
        familyId: 'METFORMIN',
        productSkus: ['METFORMIN'],
        tokens: ['me', 'metformin']
    },
    {
        familyId: 'FHRT',
        productSkus: ['FHRT'],
        tokens: ['fhrt']
    }
];

const LEGACY_OFFER_GROUP_RULES = [
    { id: 'SE', offerToken: 'se', tokens: ['se', 'seg', 'segm', 'semaglutide', 'semaglutide-g', 'semaglutide-gm'], productSkus: ['SEMAGLUTIDE', 'SEMAGLUTIDE-G', 'SEMAGLUTIDE-GM'] },
    { id: 'SEMD', offerToken: 'semd', tokens: ['semd', 'segmd', 'semaglutide-microdosing', 'semaglutide-g-microdosing'], productSkus: ['SEMAGLUTIDE-MICRODOSING', 'SEMAGLUTIDE-G-MICRODOSING'] },
    { id: 'TI', offerToken: 'ti', tokens: ['ti', 'tig', 'tigm', 'tirzepatide', 'tirzepatide-g', 'tirzepatide-gm'], productSkus: ['TIRZEPATIDE', 'TIRZEPATIDE-G', 'TIRZEPATIDE-GM'] },
    { id: 'TIMD', offerToken: 'timd', tokens: ['timd', 'tigmd', 'tirzepatide-microdosing', 'tirzepatide-g-microdosing'], productSkus: ['TIRZEPATIDE-MICRODOSING', 'TIRZEPATIDE-G-MICRODOSING'] },
    { id: 'OZ', offerToken: 'oz', tokens: ['oz', 'ozempic'], productSkus: ['OZEMPIC'] },
    { id: 'ZE', offerToken: 'ze', tokens: ['ze', 'zepbound'], productSkus: ['ZEPBOUND'] },
    { id: 'GL', offerToken: 'gl', tokens: ['gl', 'glutathione'], productSkus: ['GLUTATHIONE'] },
    { id: 'GLN', offerToken: 'gln', tokens: ['gln', 'glutathione-n'], productSkus: ['GLUTATHIONE-N'] },
    { id: 'NA', offerToken: 'na', tokens: ['na', 'nad-plus'], productSkus: ['NAD-PLUS'] },
    { id: 'NAN', offerToken: 'nan', tokens: ['nan', 'nad-plus-n'], productSkus: ['NAD-PLUS-N'] },
    { id: 'SER', offerToken: 'ser', tokens: ['ser', 'sermorelin'], productSkus: ['SERMORELIN'] },
    { id: 'SERO', offerToken: 'sero', tokens: ['sero', 'sermorelin-odt'], productSkus: ['SERMORELIN-ODT'] },
    { id: 'LB', offerToken: 'lb', tokens: ['lb', 'bc', 'mic-b12', 'b12'], productSkus: ['MIC-B12', 'B12'] },
    { id: 'FCE', offerToken: 'fce', tokens: ['fce', 'fce90', 'face-cream-e', 'face-cream-e-90'], productSkus: ['FACE-CREAM-E', 'FACE-CREAM-E-90'] },
    { id: 'FCT', offerToken: 'fct', tokens: ['fct', 'fct90', 'face-cream-r', 'face-cream-r-90', 'face-cream-t', 'face-cream-t-90'], productSkus: ['FACE-CREAM-T', 'FACE-CREAM-T-90'] },
    { id: 'ME', offerToken: 'me', tokens: ['me', 'metformin'], productSkus: ['METFORMIN'] },
    { id: 'FHRT', offerToken: 'fhrt', tokens: ['fhrt'], productSkus: ['FHRT'] }
];

export const normalizeIdentityToken = (value) => {
    return isStringNotEmpty(value) ? toLower(value.trim()) : '';
};

const findLegacyFamilyRule = (value) => {
    const normalizedValue = normalizeIdentityToken(value);
    if (!isStringNotEmpty(normalizedValue)) {
        return null;
    }

    return LEGACY_PRODUCT_FAMILY_RULES.find((rule) => {
        return rule.productSkus.some((productSku) => normalizeIdentityToken(productSku) === normalizedValue)
            || rule.tokens.some((token) => normalizeIdentityToken(token) === normalizedValue);
    }) ?? null;
};

const findLegacyOfferGroupRule = (value) => {
    const normalizedValue = normalizeIdentityToken(value);
    if (!isStringNotEmpty(normalizedValue)) {
        return null;
    }

    return LEGACY_OFFER_GROUP_RULES.find((rule) => {
        return rule.productSkus.some((productSku) => normalizeIdentityToken(productSku) === normalizedValue)
            || rule.tokens.some((token) => normalizeIdentityToken(token) === normalizedValue);
    }) ?? null;
};

export const getLegacyProductFamilyId = (value) => {
    return findLegacyFamilyRule(value)?.familyId ?? null;
};

export const getLegacyOfferGroup = (value) => {
    const rule = findLegacyOfferGroupRule(value);
    return isObjectNotEmpty(rule) ? { id: rule.id, offerToken: rule.offerToken, legacyTokens: rule.tokens } : null;
};

export const getProductFamilyById = (productFamilyId, productFamilies = []) => {
    return isListNotEmpty(productFamilies) && isStringNotEmpty(productFamilyId)
        ? productFamilies.find((productFamily) => productFamily.id === productFamilyId)
        : null;
};

export const getOfferGroupById = (offerGroupId, offerGroups = []) => {
    return isListNotEmpty(offerGroups) && isStringNotEmpty(offerGroupId)
        ? offerGroups.find((offerGroup) => offerGroup.id === offerGroupId)
        : null;
};

export const getOfferGroupByToken = (token, offerGroups = []) => {
    const normalizedToken = normalizeIdentityToken(token);
    if (!isStringNotEmpty(normalizedToken)) {
        return null;
    }

    if (isListNotEmpty(offerGroups)) {
        const exactMatch = offerGroups.find((offerGroup) => normalizeIdentityToken(offerGroup.offerToken) === normalizedToken);
        if (isObjectNotEmpty(exactMatch)) {
            return exactMatch;
        }

        const legacyMatch = offerGroups.find((offerGroup) => {
            return Array.isArray(offerGroup.legacyTokens)
                && offerGroup.legacyTokens.some((legacyToken) => normalizeIdentityToken(legacyToken) === normalizedToken);
        });
        if (isObjectNotEmpty(legacyMatch)) {
            return legacyMatch;
        }
    }

    return getLegacyOfferGroup(token);
};

export const getEffectiveProductFamilyId = (product) => {
    if (isObjectNotEmpty(product) && isStringNotEmpty(product.productFamilyId)) {
        return product.productFamilyId;
    }

    return getLegacyProductFamilyId(product?.sku ?? product?.shortSku ?? null);
};

export const normalizeSurveyFlowKey = (flowKey) => {
    if (!isStringNotEmpty(flowKey)) {
        return null;
    }

    const normalizedFlowKey = String(flowKey).trim().replace(/[\s-]+/g, '_').toUpperCase();
    if (normalizedFlowKey === 'FOLLOWUP') {
        return SURVEY_FLOW_KEYS.FOLLOW_UP;
    }

    return SURVEY_FLOW_KEY_VALUES.includes(normalizedFlowKey) ? normalizedFlowKey : null;
};

const getSurveyFlowItemForKey = (surveyFlowItems = [], flowKey = null) => {
    const normalizedFlowKey = normalizeSurveyFlowKey(flowKey);
    if (!isStringNotEmpty(normalizedFlowKey) || !isListNotEmpty(surveyFlowItems)) {
        return null;
    }

    const matchingItems = surveyFlowItems.filter((surveyFlowItem) => normalizeSurveyFlowKey(surveyFlowItem?.flowKey) === normalizedFlowKey);
    return orderBy(matchingItems, ['displayOrder'], ['asc'])[0] ?? null;
};

export const getEffectiveSurveyFlowItem = ({ program = null, product = null, productFamilies = [], flowKey = null }) => {
    const productSurveyFlowItem = getSurveyFlowItemForKey(product?.surveys, flowKey);
    if (isObjectNotEmpty(productSurveyFlowItem)) {
        return productSurveyFlowItem.enabled === false ? null : productSurveyFlowItem;
    }

    const productFamily = getProductFamilyById(getEffectiveProductFamilyId(product), productFamilies);
    const productFamilySurveyFlowItem = getSurveyFlowItemForKey(productFamily?.surveys, flowKey);
    if (isObjectNotEmpty(productFamilySurveyFlowItem)) {
        return productFamilySurveyFlowItem.enabled === false ? null : productFamilySurveyFlowItem;
    }

    const programSurveyFlowItem = getSurveyFlowItemForKey(program?.surveys, flowKey);
    if (isObjectNotEmpty(programSurveyFlowItem)) {
        return programSurveyFlowItem.enabled === false ? null : programSurveyFlowItem;
    }

    return null;
};

export const getEffectiveSurveyForFlow = ({ program = null, product = null, productFamilies = [], surveys = [], flowKey = null }) => {
    const surveyFlowItem = getEffectiveSurveyFlowItem({ program, product, productFamilies, flowKey });
    if (!isObjectNotEmpty(surveyFlowItem) || !isStringNotEmpty(surveyFlowItem.surveyId) || !isListNotEmpty(surveys)) {
        return null;
    }

    return surveys.find((survey) => survey.id === surveyFlowItem.surveyId) ?? null;
};

export const getEffectiveGoalsSurveyFlowItem = ({ program = null, product = null, productFamilies = [] }) => {
    return getEffectiveSurveyFlowItem({ program, product, productFamilies, flowKey: SURVEY_FLOW_KEYS.GOALS });
};

export const getEffectiveIntakeSurveyFlowItem = ({ program = null, product = null, productFamilies = [] }) => {
    return getEffectiveSurveyFlowItem({ program, product, productFamilies, flowKey: SURVEY_FLOW_KEYS.INTAKE });
};

export const getEffectiveConsultSurveyFlowItem = ({ program = null, product = null, productFamilies = [] }) => {
    return getEffectiveSurveyFlowItem({ program, product, productFamilies, flowKey: SURVEY_FLOW_KEYS.CONSULT });
};

export const getEffectiveFollowUpSurveyFlowItem = ({ program = null, product = null, productFamilies = [] }) => {
    return getEffectiveSurveyFlowItem({ program, product, productFamilies, flowKey: SURVEY_FLOW_KEYS.FOLLOW_UP });
};

export const getEffectiveOfferGroupId = (product) => {
    if (isObjectNotEmpty(product) && isStringNotEmpty(product.offerGroupId)) {
        return product.offerGroupId;
    }

    return getLegacyOfferGroup(product?.sku ?? product?.shortSku ?? null)?.id ?? null;
};

export const getEffectiveOfferToken = (product, offerGroups = []) => {
    if (isObjectNotEmpty(product) && isStringNotEmpty(product.offerGroupId)) {
        const offerGroup = getOfferGroupById(product.offerGroupId, offerGroups);
        if (isObjectNotEmpty(offerGroup) && isStringNotEmpty(offerGroup.offerToken)) {
            return offerGroup.offerToken;
        }
    }

    return getLegacyOfferGroup(product?.sku ?? product?.shortSku ?? null)?.offerToken ?? (isStringNotEmpty(product?.shortSku) ? toLower(product.shortSku) : null);
};

export const getProductsByOfferGroupId = (offerGroupId, products = []) => {
    if (!isStringNotEmpty(offerGroupId) || !isListNotEmpty(products)) {
        return [];
    }

    return products.filter((product) => getEffectiveOfferGroupId(product) === offerGroupId);
};

export const getProgramsAllowedForOfferGroup = (offerGroup, programs = []) => {
    if (!isObjectNotEmpty(offerGroup) || !isListNotEmpty(programs)) {
        return [];
    }

    if (isListNotEmpty(offerGroup.allowedProgramIds)) {
        return programs.filter((program) => offerGroup.allowedProgramIds.includes(program.id));
    }

    if (isStringNotEmpty(offerGroup.defaultProgramId)) {
        return programs.filter((program) => program.id === offerGroup.defaultProgramId);
    }

    return [];
};

export const getProgramForOfferGroup = ({ requestedProgramSlug = null, offerGroup = null, programs = [] }) => {
    if (!isListNotEmpty(programs)) {
        return null;
    }

    const requestedProgram = isStringNotEmpty(requestedProgramSlug)
        ? programs.find((program) => program.slug === requestedProgramSlug) ?? null
        : null;

    if (!isObjectNotEmpty(offerGroup)) {
        return requestedProgram;
    }

    const allowedPrograms = getProgramsAllowedForOfferGroup(offerGroup, programs);
    if (!isListNotEmpty(allowedPrograms)) {
        return requestedProgram;
    }

    if (isObjectNotEmpty(requestedProgram) && allowedPrograms.some((allowedProgram) => allowedProgram.id === requestedProgram.id)) {
        return requestedProgram;
    }

    if (isStringNotEmpty(offerGroup.defaultProgramId)) {
        const defaultProgram = allowedPrograms.find((allowedProgram) => allowedProgram.id === offerGroup.defaultProgramId);
        if (isObjectNotEmpty(defaultProgram)) {
            return defaultProgram;
        }
    }

    return allowedPrograms[0] ?? requestedProgram ?? null;
};

export const getDefaultProductForProgramAndOfferGroup = ({ program = null, offerGroup = null, products = [], programs = [] }) => {
    if (!isObjectNotEmpty(program) || !isListNotEmpty(products) || !isListNotEmpty(program?.productIds)) {
        return null;
    }

    let matchingProducts = products.filter((product) => program.productIds.includes(product.id));
    if (isObjectNotEmpty(offerGroup) && isStringNotEmpty(offerGroup.id)) {
        matchingProducts = matchingProducts.filter((product) => getEffectiveOfferGroupId(product) === offerGroup.id);
    }

    const orderedProducts = orderBy(matchingProducts, ['displayOrder', 'name'], ['asc', 'asc']);
    if (isListNotEmpty(orderedProducts)) {
        return orderedProducts[0];
    }

    if (isObjectNotEmpty(offerGroup) && isStringNotEmpty(offerGroup.id)) {
        const fallbackProgramCandidates = getProgramsAllowedForOfferGroup(offerGroup, programs);
        for (const allowedProgram of fallbackProgramCandidates) {
            if (allowedProgram?.id === program.id) {
                continue;
            }

            const fallbackProducts = orderBy(
                products.filter((product) => allowedProgram.productIds?.includes(product.id) && getEffectiveOfferGroupId(product) === offerGroup.id),
                ['displayOrder', 'name'],
                ['asc', 'asc']
            );
            if (isListNotEmpty(fallbackProducts)) {
                return fallbackProducts[0];
            }
        }
    }

    return null;
};

export const resolveOfferContext = ({ requestedProgramSlug = null, requestedProductValue = null, products = [], programs = [], offerGroups = [] }) => {
    const normalizedProductValue = normalizeIdentityToken(requestedProductValue);
    const exactProduct = isStringNotEmpty(requestedProductValue)
        ? products.find((product) => normalizeIdentityToken(product.sku) === normalizedProductValue) ?? null
        : null;
    const offerGroup = isStringNotEmpty(requestedProductValue)
        ? getOfferGroupByToken(requestedProductValue, offerGroups)
        : null;

    let resolvedProgram = getProgramForOfferGroup({
        requestedProgramSlug,
        offerGroup,
        programs
    });

    let resolvedProduct = null;
    if (isObjectNotEmpty(exactProduct)) {
        const exactProductOfferGroupId = getEffectiveOfferGroupId(exactProduct);
        if (!isObjectNotEmpty(offerGroup) || exactProductOfferGroupId === offerGroup.id) {
            resolvedProduct = exactProduct;
        }

        if (!isObjectNotEmpty(resolvedProgram)) {
            resolvedProgram = programs.find((program) => isListNotEmpty(program?.productIds) && program.productIds.includes(exactProduct.id)) ?? null;
        }
    }

    if (!isObjectNotEmpty(resolvedProduct) && (isObjectNotEmpty(offerGroup) || isObjectNotEmpty(resolvedProgram))) {
        resolvedProduct = getDefaultProductForProgramAndOfferGroup({
            program: resolvedProgram,
            offerGroup,
            products,
            programs
        });
    }

    if (!isObjectNotEmpty(resolvedProgram) && isObjectNotEmpty(resolvedProduct)) {
        resolvedProgram = programs.find((program) => isListNotEmpty(program?.productIds) && program.productIds.includes(resolvedProduct.id)) ?? null;
    }

    return {
        offerGroup,
        program: resolvedProgram,
        product: resolvedProduct,
        offerGroupId: offerGroup?.id ?? getEffectiveOfferGroupId(resolvedProduct),
        offerToken: offerGroup?.offerToken ?? getEffectiveOfferToken(resolvedProduct, offerGroups)
    };
};

export const getProgramsByDuplicateEnrollmentGroupId = (duplicateEnrollmentGroupId, programs = []) => {
    return isListNotEmpty(programs) && isStringNotEmpty(duplicateEnrollmentGroupId)
        ? programs.filter((program) => program.duplicateEnrollmentGroupId === duplicateEnrollmentGroupId)
        : [];
};

export const getDuplicateScopeProgramIds = (program, programs = []) => {
    if (!isObjectNotEmpty(program) || !isStringNotEmpty(program.id)) {
        return [];
    }

    if (program.duplicateCheckScope === 'NONE') {
        return [];
    }

    if (program.duplicateCheckScope === 'ENROLLMENT_GROUP' && isStringNotEmpty(program.duplicateEnrollmentGroupId)) {
        return getProgramsByDuplicateEnrollmentGroupId(program.duplicateEnrollmentGroupId, programs).map((matchingProgram) => matchingProgram.id);
    }

    if (program.duplicateCheckScope === 'PROGRAM') {
        return [program.id];
    }

    return [];
};

export const getProductForIdentityComparison = (entity, products = []) => {
    if (!isObjectNotEmpty(entity) || !isListNotEmpty(products)) {
        return null;
    }

    if (isStringNotEmpty(entity.productId)) {
        const productById = products.find((product) => product.id === entity.productId);
        if (isObjectNotEmpty(productById)) {
            return productById;
        }
    }

    if (isStringNotEmpty(entity.productSku)) {
        const productBySku = products.find((product) => product.sku === entity.productSku);
        if (isObjectNotEmpty(productBySku)) {
            return productBySku;
        }
    }

    if (isListNotEmpty(entity.lineItems)) {
        const matchingLineItem = entity.lineItems.find((lineItem) => isStringNotEmpty(lineItem?.sku));
        if (isObjectNotEmpty(matchingLineItem)) {
            return products.find((product) => product.sku === matchingLineItem.sku) ?? null;
        }
    }

    return null;
};

const getProductRouteValue = (product) => {
    return isStringNotEmpty(product?.route) ? toUpper(product.route) : null;
};

export const doesEntityMatchFamilyRule = ({ targetProduct, existingProduct, productFamilies = [] }) => {
    if (!isObjectNotEmpty(targetProduct) || !isObjectNotEmpty(existingProduct)) {
        return false;
    }

    const targetFamilyId = getEffectiveProductFamilyId(targetProduct);
    const existingFamilyId = getEffectiveProductFamilyId(existingProduct);

    if (!isStringNotEmpty(targetFamilyId) || !isStringNotEmpty(existingFamilyId)) {
        return targetProduct.id === existingProduct.id || targetProduct.sku === existingProduct.sku;
    }

    if (targetFamilyId !== existingFamilyId) {
        return false;
    }

    const productFamily = getProductFamilyById(targetFamilyId, productFamilies);
    if (isObjectNotEmpty(productFamily) && productFamily.allowMultipleActiveSubscriptions === true) {
        return false;
    }

    const duplicateKeyFields = isListNotEmpty(productFamily?.duplicateKeyFields) ? productFamily.duplicateKeyFields : ['productFamilyId'];
    if (duplicateKeyFields.includes('route')) {
        return getProductRouteValue(targetProduct) === getProductRouteValue(existingProduct);
    }

    return true;
};

export const getDuplicateEnrollmentDisplayContext = ({ targetProgram, targetProduct, blockingEntity, products = [], productFamilies = [] }) => {
    const blockingProduct = getProductForIdentityComparison(blockingEntity, products);
    const targetFamilyId = getEffectiveProductFamilyId(targetProduct);
    const blockingFamilyId = getEffectiveProductFamilyId(blockingProduct);
    const productFamily = isStringNotEmpty(targetFamilyId)
        ? getProductFamilyById(targetFamilyId, productFamilies)
        : null;
    const isProductFamilyDuplicate = targetProgram?.duplicateCheckMode === 'FAMILY_RULE'
        && isStringNotEmpty(targetFamilyId)
        && targetFamilyId === blockingFamilyId
        && doesEntityMatchFamilyRule({ targetProduct, existingProduct: blockingProduct, productFamilies });

    return {
        blockingProduct,
        productFamily,
        isProductFamilyDuplicate,
    };
};

const getLegacyDuplicateMode = (program) => {
    if (!isObjectNotEmpty(program) || !isStringNotEmpty(program.slug)) {
        return null;
    }

    if (['wl', 'gmd'].includes(toLower(program.slug))) {
        return { scope: 'ENROLLMENT_GROUP', programSlugs: ['wl', 'gmd'], mode: 'ANY_ACTIVE' };
    }

    if (['l', 'ms', 'sc', 'fhrt'].includes(toLower(program.slug))) {
        return { scope: 'PROGRAM', mode: 'SAME_SKU' };
    }

    return null;
};

export const getDuplicateProgramIds = (program, programs = []) => {
    if (hasExplicitDuplicateConfig(program) && program.duplicateCheckScope === 'NONE') {
        return [];
    }

    const scopedProgramIds = getDuplicateScopeProgramIds(program, programs);
    if (isListNotEmpty(scopedProgramIds)) {
        return scopedProgramIds;
    }

    if (hasExplicitDuplicateConfig(program)) {
        return [];
    }

    const legacyMode = getLegacyDuplicateMode(program);
    if (legacyMode?.scope === 'ENROLLMENT_GROUP' && isListNotEmpty(legacyMode.programSlugs)) {
        return programs
            .filter((candidateProgram) => legacyMode.programSlugs.includes(toLower(candidateProgram.slug)))
            .map((candidateProgram) => candidateProgram.id);
    }

    return isObjectNotEmpty(program) && isStringNotEmpty(program.id) ? [program.id] : [];
};

const isModernDuplicateConfigEnabled = (program) => {
    return isObjectNotEmpty(program)
        && isStringNotEmpty(program.duplicateCheckScope)
        && program.duplicateCheckScope !== 'NONE'
        && isStringNotEmpty(program.duplicateCheckMode);
};

const hasExplicitDuplicateConfig = (program) => {
    return isObjectNotEmpty(program) && isStringNotEmpty(program.duplicateCheckScope);
};

export const findBlockingEntityForDuplicateRule = ({ targetProgram, targetProduct, entities = [], products = [], programs = [], productFamilies = [] }) => {
    if (!isObjectNotEmpty(targetProgram) || !isListNotEmpty(entities)) {
        return null;
    }

    if (hasExplicitDuplicateConfig(targetProgram) && targetProgram.duplicateCheckScope === 'NONE') {
        return null;
    }

    if (isModernDuplicateConfigEnabled(targetProgram)) {
        if (targetProgram.duplicateCheckMode === 'ANY_ACTIVE') {
            return entities[0] ?? null;
        }

        if (targetProgram.duplicateCheckMode === 'FAMILY_RULE' && isObjectNotEmpty(targetProduct)) {
            return entities.find((entity) => {
                const existingProduct = getProductForIdentityComparison(entity, products);
                return doesEntityMatchFamilyRule({ targetProduct, existingProduct, productFamilies });
            }) ?? null;
        }

        return null;
    }

    const legacyMode = getLegacyDuplicateMode(targetProgram);
    if (!isObjectNotEmpty(legacyMode)) {
        return null;
    }

    if (legacyMode.mode === 'ANY_ACTIVE') {
        return entities[0] ?? null;
    }

    if (legacyMode.mode === 'SAME_SKU' && isObjectNotEmpty(targetProduct) && isStringNotEmpty(targetProduct.sku)) {
        return entities.find((entity) => {
            const existingProduct = getProductForIdentityComparison(entity, products);
            return isObjectNotEmpty(existingProduct) && existingProduct.sku === targetProduct.sku;
        }) ?? null;
    }

    return null;
};

const getDefaultProgramForProduct = (targetProduct, programs = []) => {
    if (!isObjectNotEmpty(targetProduct) || !isStringNotEmpty(targetProduct.id) || !isListNotEmpty(programs)) {
        return null;
    }

    return programs.find((program) => isListNotEmpty(program?.productIds) && program.productIds.includes(targetProduct.id)) ?? null;
};

export const getDuplicateRuleEligibleProductSkus = ({ availableProductSkus = [], existingEntities = [], currentProgram = null, products = [], programs = [], productFamilies = [] }) => {
    if (!isListNotEmpty(availableProductSkus)) {
        return [];
    }

    return availableProductSkus.filter((availableProductSku) => {
        const targetProduct = products.find((product) => product.sku === availableProductSku) ?? null;
        const targetProgram = getDefaultProgramForProduct(targetProduct, programs) ?? currentProgram;
        if (!isObjectNotEmpty(targetProduct) || !isObjectNotEmpty(targetProgram)) {
            return false;
        }

        const duplicateProgramIds = getDuplicateProgramIds(targetProgram, programs);
        const scopedEntities = isListNotEmpty(duplicateProgramIds)
            ? existingEntities.filter((entity) => duplicateProgramIds.includes(entity.programId))
            : [];
        const blockingEntity = findBlockingEntityForDuplicateRule({
            targetProgram,
            targetProduct,
            entities: scopedEntities,
            products,
            programs,
            productFamilies
        });

        return !isObjectNotEmpty(blockingEntity);
    });
};

export const orderEntitiesByMostRecent = (entities = []) => {
    return isListNotEmpty(entities) ? orderBy(entities, ['modifiedAt', 'createdAt'], ['desc', 'desc']) : [];
};
